import {
  InfoCircleOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Edit, useForm } from "@refinedev/antd";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  Button,
  Checkbox,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Spin,
  Upload,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SpotifyIdValidator } from "services/community/spotifyIdValidator";
import { updateCommunityOwner } from "services/community/updateCommunityOwner";
import { getSubscriptionTiers } from "services/subscription/getSubscriptionTiers";
import {
  getAddons,
  AddonDTO,
  AddOnType,
} from "services/subscription/getAddons";
import { validate as uuidValidate } from "uuid";
import { CommunityUpdateDTO } from "../../interfaces";
import {
  SubscriptionTierDTO,
  SubscriptionTierType,
} from "../../services/subscription/getSubscriptionTiers";
import { updateCommunityUseCase } from "../../usecases/community/updateCommunityUseCase";
import { Status, STATUS_ITEMS } from "utils/constants";

export const CommunityEdit: React.FC<IResourceComponentsProps> = () => {
  const [submiting, setSubmitting] = useState<boolean>(false);
  const { formProps, form, formLoading } = useForm<CommunityUpdateDTO>({
    redirect: "list",
    warnWhenUnsavedChanges: false,
    queryOptions: {
      retry: 0,
    },
  });

  const params = useParams();

  const [communityId, setCommunityId] = useState<string>("");

  const [isFileFormatValid, setIsFileFormatValid] = useState(true);
  const [isFileSizeValid, setIsFileSizeValid] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [spotifyId, setSpotifyId] = useState<string>("");
  const [showSpotifyUrlWarning, setShowSpotifyUrlWarning] = useState(false);
  const [
    spotifyIdFeatureWarningDiscarded,
    setSpotifyIdFeatureWarningDiscarded,
  ] = useState(false);
  const [status, setStatus] = useState<String>("");
  const [tiersLoading, setTiersLoading] = useState<boolean>(true);
  const [stripeCustomerId, setStripeCustomerId] = useState<String>("");
  const [subscriptionTiers, setSubscriptionTiers] = useState<
    SubscriptionTierDTO[]
  >([]);
  const [addons, setAddons] = useState<AddonDTO[]>([]);
  const [addonsLoading, setAddonsLoading] = useState<boolean>(true);

  useEffect(() => {
    setSpotifyId(form.getFieldValue("spotifyId"));
  }, [form.getFieldValue("spotifyId")]);

  useEffect(() => {
    setCommunityId(params.id || "");
  }, [params, form]);

  useEffect(() => {
    setStatus(form.getFieldValue("status"));
  }, [form.getFieldValue("status")]);

  useEffect(() => {
    setStripeCustomerId(form.getFieldValue("stripeCustomerId"));
  }, [form.getFieldValue("stripeCustomerId")]);

  useEffect(() => {
    const fetchSubscriptionTiers = async () => {
      try {
        setTiersLoading(true);
        const response = await getSubscriptionTiers();
        setSubscriptionTiers(response.data || []);
      } catch (error: any) {
        notification.error({
          message: "Failed to load subscription tiers",
          description: error?.response?.data?.message || "An error occurred",
        });
      } finally {
        setTiersLoading(false);
      }
    };

    fetchSubscriptionTiers();
  }, []);

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        setAddonsLoading(true);
        const response = await getAddons();
        setAddons(response.data || []);
      } catch (error: any) {
        notification.error({
          message: "Failed to load addons",
          description: error?.response?.data?.message || "An error occurred",
        });
      } finally {
        setAddonsLoading(false);
      }
    };

    fetchAddons();
  }, []);

  useEffect(() => {
    if (formLoading || addons.length === 0) return;
    const raw: any[] =
      form.getFieldValue("subscriptionModules") ??
      formProps.initialValues?.subscriptionModules ??
      [];
    const addonIds = new Set(addons.map((a) => a.id));
    const moduleIds = raw.map((m) => (typeof m === "string" ? m : m?.id));
    const checked = moduleIds.filter((id) => addonIds.has(id));
    form.setFieldsValue({ subscriptionModules: checked });
  }, [formLoading, addons]);

  const handleChange = async (value: String) => {
    setStatus(value);
  };

  const handleFormSubmit = async (values: any) => {
    if (!isFileFormatValid) {
      notification.error({
        message: "Invalid file type",
        description: "You can only upload .jpg file!",
      });
      return false;
    }
    if (!isFileSizeValid) {
      notification.error({
        message: "Invalid size",
        description: "Please upload a file no larger than 2MB.",
      });
      return false;
    }
    if (uploadedFile) {
      form.setFieldsValue({
        image: uploadedFile,
      });
    }

    await handleLinksSubmitValue();

    return true;
  };

  const handleLinksSubmitValue = async () => {
    let links: any = { ...form.getFieldValue("links") };

    for (const key in links) {
      if (links.hasOwnProperty(key)) {
        if (links[key] === "") {
          links[key] = null;
        }
      }
    }

    const allNull = Object.values(links).every((value) => value === null);
    if (allNull) {
      links = null;
    }

    form.setFieldsValue({
      links: links,
    });
  };

  async function onFinish(values: any) {
    try {
      setSubmitting(true);
      if (
        !(values?.image instanceof File) ||
        values?.image?.file?.status === "removed"
      ) {
        values.image = undefined;
      }

      if (values.ownerId !== formProps.initialValues?.ownerId) {
        const isOwnerIdValid = uuidValidate(values.ownerId);
        if (!isOwnerIdValid) {
          notification.error({
            message: "Invalid owner ID",
            description: "Please add a valid uuid for the owner ID",
          });
          return;
        }

        await updateCommunityOwner(communityId, values.ownerId);
      }

      const initialVipConfig = {
        enabled: formProps.initialValues?.vipConfiguration?.enabled,
        price: formProps.initialValues?.vipConfiguration?.price,
        fee: formProps.initialValues?.vipConfiguration?.fee,
        stripeAccountId:
          formProps.initialValues?.vipConfiguration?.stripeAccountId,
      };
      await updateCommunityUseCase(communityId, values, initialVipConfig);

      notification.success({
        message: "Community updated successfully",
      });
      setTimeout(() => {
        window.location.href = "/communities";
      }, 1000);
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.message,
        description: error?.response?.status,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      canDelete={false}
      headerButtons={<></>}
    >
      <Form
        form={form}
        {...formProps}
        layout="vertical"
        onFinish={onFinish}
        onSubmitCapture={handleFormSubmit}
      >
        <Form.Item
          label={"Name"}
          name="name"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Slug"
          name="slug"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="Owner"
          name="ownerId"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <div
          style={{
            marginTop: "5px",
            marginBottom: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span>Image</span>
          {!uploadedFile && formProps.initialValues?.image?.webp?.main && (
            <Image
              src={formProps.initialValues?.image?.webp?.main}
              width={200}
              style={{ marginTop: "10px" }}
            />
          )}
        </div>
        <Form.Item name="image">
          <Upload
            name="image"
            listType="picture"
            accept=".jpg"
            maxCount={1}
            beforeUpload={(file) => {
              if (file.type !== "image/jpeg" || !file.name.endsWith(".jpg")) {
                setIsFileFormatValid(false);
                return true; // Unexpectedly, this will show file in red color as an error
              }

              if (file.size > 2 * 1024 * 1024) {
                setIsFileSizeValid(false);
                return true; // Unexpectedly, this will show file in red color as an error
              }

              setUploadedFile(file);

              setIsFileSizeValid(true);
              setIsFileFormatValid(true);
              return false; // Unexpectedly, this will show file in gray color as a success
            }}
            onRemove={() => {
              setUploadedFile(null);
            }}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
        <div
          style={{
            marginTop: "-10px",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span style={{ fontSize: "12px" }}>
            Only <b>.jpg</b> files are permitted for the image. (.jpeg, .png,
            .webp and any other extension <b>are NOT allowed</b>)
          </span>
          <span style={{ fontSize: "12px" }}>
            Maximum file size is <b>2MB</b>.
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#f9fbfc",
            padding: "1rem 1rem 0",
            marginBottom: "1rem",
          }}
        >
          <Form.Item
            label="Hidden"
            name="hidden"
            valuePropName="checked"
            rules={[
              {
                required: false,
                type: "boolean",
              },
            ]}
          >
            <Checkbox
              title={"Hidden"}
              name="hidden"
              onChange={(event) => {
                if (
                  !event.target.checked &&
                  !form.getFieldValue("spotifyId") &&
                  !spotifyIdFeatureWarningDiscarded
                ) {
                  Modal.warning({
                    title: "Warning",
                    content:
                      "Community will be accesible for users and, without a Spotify ID, it will not have the streaks feature enabled",
                    onOk() {
                      setSpotifyIdFeatureWarningDiscarded(true);
                    },
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Searchable"
            name="searchable"
            valuePropName="checked"
            rules={[
              {
                required: false,
                type: "boolean",
              },
            ]}
          >
            <Checkbox
              title={"Searchable"}
              name="searchable"
              onChange={(event) => {
                if (
                  event.target.checked &&
                  !form.getFieldValue("spotifyId") &&
                  !spotifyIdFeatureWarningDiscarded
                ) {
                  Modal.warning({
                    title: "Warning",
                    content:
                      "Community will be accesible for users and, without a Spotify ID, it will not have the streaks feature enabled",
                    onOk() {
                      setSpotifyIdFeatureWarningDiscarded(true);
                    },
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Verified"
            name="verified"
            valuePropName="checked"
            rules={[
              {
                required: false,
              },
            ]}
            initialValue={true}
          >
            <Checkbox
              title={"Verified"}
              name="verified"
              defaultChecked={true}
            />
          </Form.Item>
        </div>
        <Form.Item
          label="Spotify ID"
          name="spotifyId"
          rules={[
            {
              required: false,
              validator: async (_, value: string) => {
                const validator = new SpotifyIdValidator();
                if (validator.validate(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(validator.errorMessage()));
              },
            },
          ]}
          style={{ marginBottom: showSpotifyUrlWarning ? "0" : "10px" }}
        >
          <Input
            onChange={(e) => {
              setShowSpotifyUrlWarning(true);
              setSpotifyId(e.target.value);
            }}
          />
        </Form.Item>
        {showSpotifyUrlWarning && (
          <p style={{ fontSize: "12px", marginBottom: "10px" }}>
            before saving changes make sure the profile url is correct{" "}
            <a
              href={`https://open.spotify.com/artist/${
                spotifyId || form.getFieldValue("spotifyId")
              }`}
              target="_blank"
              rel="noreferrer"
            >{`https://open.spotify.com/artist/${
              spotifyId || form.getFieldValue("spotifyId")
            }`}</a>
          </p>
        )}
        <h4
          style={{
            textAlign: "center",
            marginTop: "3rem",
          }}
        >
          Community Subscriptions
        </h4>

        <div
          style={{
            marginTop: "5px",
            display: "flex",
            flexDirection: "row",
            gap: 40,
          }}
        >
          <Form.Item
            label="Tier"
            name="tierId"
            rules={[
              {
                required: false,
              },
            ]}
            style={{
              width: "50%",
            }}
          >
            <Select
              loading={tiersLoading}
              placeholder="Select a subscription tier"
              notFoundContent={
                tiersLoading ? <Spin size="small" /> : "No tiers available"
              }
              onChange={(value) => {
                const selectedTier = subscriptionTiers.find(
                  (t) => t.id === value,
                );
                if (!selectedTier) return;

                if (selectedTier.type === SubscriptionTierType.FULL_SUITE) {
                  const allAddonIds = addons
                    .filter((addon) => addon.priceMonthly > 0)
                    .map((addon) => addon.id);
                  form.setFieldsValue({ subscriptionModules: allAddonIds });
                } else if (
                  selectedTier.type === SubscriptionTierType.STARTER_BASE
                ) {
                  const walletPassAddon = addons.find(
                    (a) => a.type === AddOnType.WALLET_PASS,
                  );
                  const initialModulesRaw: any[] =
                    formProps.initialValues?.subscriptionModules || [];
                  const initialModuleIds = initialModulesRaw.map((m: any) =>
                    typeof m === "string" ? m : m?.id,
                  );

                  const newModules = new Set<string>();
                  if (walletPassAddon) {
                    newModules.add(walletPassAddon.id);
                  }
                  initialModuleIds.forEach((id: string) => {
                    if (id) newModules.add(id);
                  });

                  form.setFieldsValue({
                    subscriptionModules: Array.from(newModules),
                  });
                }
              }}
            >
              {subscriptionTiers.map((tier) => (
                <Select.Option value={tier.id} key={tier.id}>
                  {tier.displayName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Billing Interval"
            name="billingInterval"
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Radio.Group disabled>
              <Radio value="monthly">Monthly</Radio>
              <Radio value="annual">Annual</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        <div
          style={{
            marginTop: "5px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Form.Item
            label="Status"
            name="status"
            rules={[
              {
                required: false,
                type: "enum",
                enum: Object.keys(Status),
              },
            ]}
          >
            <Select onChange={(e) => handleChange(e)}>
              {STATUS_ITEMS.map((item) => (
                <Select.Option value={item.key} key={item.key}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div
          style={{
            marginTop: "5px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Form.Item
            label="Stripe Customer ID"
            name="stripeCustomerId"
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input
              type="text"
              onChange={(e) => setStripeCustomerId(e.target.value)}
            />
          </Form.Item>
        </div>

        <Form.Item noStyle dependencies={["tierId"]}>
          {() => {
            const currentTierId = form.getFieldValue("tierId");
            const isFullSuite =
              subscriptionTiers.find((t) => t.id === currentTierId)?.type ===
              SubscriptionTierType.FULL_SUITE;

            return (
              <div
                style={{
                  marginTop: "5px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Form.Item label="Addons" name="subscriptionModules">
                  <Checkbox.Group style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {addons
                        .filter((addon) => addon.priceMonthly > 0)
                        .map((addon) => (
                          <Checkbox
                            key={addon.id}
                            value={addon.id}
                            disabled={
                              addon.type === AddOnType.WALLET_PASS ||
                              isFullSuite
                            }
                          >
                            {addon.displayName} (${addon.priceMonthly}/mo)
                          </Checkbox>
                        ))}
                    </div>
                  </Checkbox.Group>
                </Form.Item>
              </div>
            );
          }}
        </Form.Item>
        <h4
          style={{
            textAlign: "center",
            marginTop: "3rem",
          }}
        >
          Community Livestream
        </h4>

        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            backgroundColor: "#f9fbfc",
            padding: "1rem 1rem 0",
            marginBottom: "1rem",
          }}
        >
          <Form.Item
            label="Enabled"
            name="livestreamEnabled"
            valuePropName="checked"
            rules={[
              {
                required: false,
                type: "boolean",
              },
            ]}
          >
            <Checkbox title={"Enabled"} name="livestreamEnabled" />
          </Form.Item>
          <Form.Item
            label="Unlimited Viewers"
            name="livestreamUnlimitedViewers"
            valuePropName="checked"
            rules={[
              {
                required: false,
                type: "boolean",
              },
            ]}
          >
            <Checkbox
              title={"Unlimited Viewers"}
              name="livestreamUnlimitedViewers"
            />
          </Form.Item>
        </div>

        <div
          style={{
            display: "flex",
            marginTop: "5px",
            justifyContent: "space-around",
          }}
        >
          <Form.Item
            label="Allowed Time Per Month"
            name="livestreamAllowedTimePerMonth"
            rules={[
              {
                required: false,
                type: "integer",
              },
            ]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label="Max Time Per Session"
            name="livestreamMaxTimePerSession"
            rules={[
              {
                required: false,
                type: "integer",
              },
            ]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label="Max Viewers"
            name="livestreamMaxViewers"
            rules={[
              {
                required: false,
                type: "integer",
              },
            ]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label="Used Time This Month"
            name="livestreamUsedTimeThisMonth"
            rules={[
              {
                required: false,
                type: "integer",
              },
            ]}
          >
            <InputNumber disabled />
          </Form.Item>
        </div>

        <h4
          style={{
            textAlign: "center",
            marginTop: "3rem",
          }}
        >
          Vip Subscription
        </h4>

        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            padding: "2rem 1rem 0",
            marginBottom: "1rem",
          }}
        >
          <Form.Item
            label="Enabled"
            name={["vipConfiguration", "enabled"]}
            valuePropName="checked"
            rules={[
              {
                required: false,
                type: "boolean",
              },
            ]}
          >
            <Checkbox title={"Enabled"} />
          </Form.Item>
          <Form.Item
            label="Price"
            name={["vipConfiguration", "price"]}
            dependencies={[["vipConfiguration", "enabled"]]}
            rules={[
              ({ getFieldValue }) => ({
                required: getFieldValue(["vipConfiguration", "enabled"]),
                message: "Please enter price",
              }),
              {
                validator(_, value) {
                  if (value == null) return Promise.resolve(); // handled by required
                  if (isNaN(value))
                    return Promise.reject("Price must be a number");
                  if (value < 29.99)
                    return Promise.reject("Minimum price is $29.99");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="Enter price"
            />
          </Form.Item>

          <Form.Item
            label="Fee (%)"
            name={["vipConfiguration", "fee"]}
            dependencies={[["vipConfiguration", "enabled"]]}
            rules={[
              ({ getFieldValue }) => ({
                required: getFieldValue(["vipConfiguration", "enabled"]),
                message: "Please enter fee",
              }),
              {
                validator(_, value) {
                  if (value == null) return Promise.resolve();
                  if (isNaN(value))
                    return Promise.reject("Fee must be a number");
                  if (value < 10) return Promise.reject("Minimum fee is 10%");
                  if (value > 100)
                    return Promise.reject("Fee cannot exceed 100%");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.1}
              style={{ width: "100%" }}
              placeholder="Enter fee %"
            />
          </Form.Item>

          <Form.Item label="Active Subscriber">
            {form.getFieldValue(["vipConfiguration", "activeSubscriber"]) ?? 0}
          </Form.Item>

          <Form.Item label="Total revenue">
            $ {form.getFieldValue(["vipConfiguration", "totalRevenue"]) ?? 0}
          </Form.Item>
          <Form.Item
            label="Stripe Account Id"
            name={["vipConfiguration", "stripeAccountId"]}
            dependencies={["vipConfiguration", "enabled"]}
            rules={[
              ({ getFieldValue }) => ({
                required: getFieldValue(["vipConfiguration", "enabled"]),
                message: "Please enter stripe account id",
              }),
            ]}
          >
            <Input placeholder="acct_1234567890" />
          </Form.Item>
        </div>

        <Form.Item>
          <Button
            loading={submiting}
            icon={<SaveOutlined />}
            type="primary"
            size="large"
            htmlType="submit"
            style={{ float: "right" }}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </Edit>
  );
};
